import { faChevronCircleLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { ReactElement } from 'react';
import type { To, Location } from 'react-router';
import { useNavigate, useLocation } from 'react-router';
import { ExternalLink, Link } from 'src/components/navigation/link';
import { setLocationHref } from 'src/utils/location-usage';

type WithOptionalReferrerState = { referrer?: ReferrerState } | void;

type ReferrerLocation = Partial<Location<WithOptionalReferrerState>>;

export type ReferrerState = {
  location: ReferrerLocation | string;
  label: string;
};

export type Referrer = ReferrerState & {
  /**
   * Creates a "Back to ..." link that points to the parent page.
   */
  backLink: ReactElement;
  /**
   * Creates a cancel "button" that points to the parent page.
   */
  cancelLink: ReactElement;
  /**
   * Navigate to the parent page.
   */
  goTo: () => void;
  /**
   * Merges the referrer state of the parent page into the given location descriptor to be used in a link.
   *
   * Note: While we usually work with a "location" object, links handle
   * the state separate from all other fields. Those other fields are called
   * a "to" object.
   */
  getLinkProps: (location: Partial<Location<{}>> | string) => {
    to: To;
    state: { referrer: ReferrerState }; // note: we will also merge `location.state` into this
  };
};

/**
 * Resolves the current page referrer and gives you helpers to navigate back to it.
 * Use it on a child page to read the passed referrer, fall back to a default parent page, and drive back links, cancel buttons, or redirects after submit.
 * See detailed documentation: https://storybook.service.prisma-capacity.cloud/master/?path=/docs/developer-navigation-with-referrers--docs
 */
export function useReferrer(defaultReferrer: ReferrerState): Referrer {
  const { state } = useLocation<WithOptionalReferrerState>();
  const referrerState = state?.referrer ?? defaultReferrer;

  // additional span is used, so it can be safely used as a child of <Stack flow="row"/>
  // without making the link go over the whole page
  const backLink = (
    <span>
      {isExternal(referrerState.location) ? (
        <ExternalLink href={referrerState.location}>
          <FontAwesomeIcon icon={faChevronCircleLeft} aria-hidden /> Back to{' '}
          {referrerState.label}
        </ExternalLink>
      ) : (
        <Link
          to={referrerState.location}
          state={referrerState.location.state}
          data-testid="referrer-back-link"
        >
          <FontAwesomeIcon icon={faChevronCircleLeft} aria-hidden /> Back to{' '}
          {referrerState.label}
        </Link>
      )}
    </span>
  );

  const cancelLink = isExternal(referrerState.location) ? (
    <ExternalLink href={referrerState.location} mode="button-secondary">
      Cancel
    </ExternalLink>
  ) : (
    <Link
      to={referrerState.location}
      state={referrerState.location.state}
      mode="button-secondary"
    >
      Cancel
    </Link>
  );

  const navigate = useNavigate();

  const goTo = () => {
    if (isExternal(referrerState.location)) {
      setLocationHref(referrerState.location);
    } else {
      navigate(referrerState.location, { state: referrerState.location.state });
    }
  };

  const getLinkProps = (location: Partial<Location<{}>> | string) => {
    if (typeof location === 'string') {
      return {
        to: location,
        state: { referrer: referrerState },
      };
    } else {
      const { state, ...to } = location;
      return {
        to,
        state: { ...state, referrer: referrerState },
      };
    }
  };

  return { ...referrerState, backLink, cancelLink, goTo, getLinkProps };
}

type UseSaveReferrerOptions = {
  label: string;
  // TODO: Add support for anchor in a follow-up merge request.
  // anchor: string;
};

export type SavedReferrer = ReferrerState & {
  /**
   * Merges the referrer state of the current page into the given location descriptor to be used in a link.
   *
   * Note: While we usually work with a "location" object, links handle
   * the state separate from all other fields. Those other fields are called
   * a "to" object.
   */
  getLinkProps: (location: Partial<Location<{}>> | string) => {
    to: To;
    state: { referrer: ReferrerState }; // note: we will also merge `location.state` into this
  };
};

/**
 * Saves the current page as a referrer so child pages can remember where the user came from.
 * Use it on a parent page and pass it forward with `getLinkProps(...)` when linking to details, edit pages or other child pages.
 * See detailed documentation: https://storybook.service.prisma-capacity.cloud/master/?path=/docs/developer-navigation-with-referrers--docs
 */
export function useSaveReferrer({
  label,
}: UseSaveReferrerOptions): SavedReferrer {
  const location = useLocation<WithOptionalReferrerState>();
  const referrerState: ReferrerState = { location, label };

  const getLinkProps = (location: Partial<Location<{}>> | string) => {
    if (typeof location === 'string') {
      return {
        to: location,
        state: { referrer: referrerState },
      };
    } else {
      const { state, ...to } = location;
      return {
        to,
        state: { ...state, referrer: referrerState },
      };
    }
  };

  return { ...referrerState, getLinkProps };
}

function isExternal(location: ReferrerLocation | string): location is string {
  return (
    typeof location === 'string' &&
    (location.startsWith('https://') || location.startsWith('http://'))
  );
}
